import React from 'react';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Animated grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 107, 53, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 107, 53, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px'
        }}></div>
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-orange-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-2 shadow-sm animate-fade-in">
              <div className="w-2 h-2 bg-[#ff6b35] rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-[#ff6b35]">Innovación y Tecnología</span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4 animate-fade-in-up">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-gray-900">Conecta tu</span>
                <br />
                <span className="text-gray-900">negocio </span>
                <span className="inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff6b35] via-[#f97316] to-[#fb923c]">
                    con el futuro
                  </span>
                  <div className="h-1 bg-gradient-to-r from-[#ff6b35] via-[#f97316] to-transparent rounded-full mt-2"></div>
                </span>
              </h1>
            </div>

            {/* Description */}
            <p className="text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed animate-fade-in-up delay-200">
              Impulsamos la transformación digital con soluciones de <span className="text-[#ff6b35] font-semibold">Desarrollo</span>, <span className="text-[#ff6b35] font-semibold">Cloud</span> e <span className="text-[#ff6b35] font-semibold">IA</span> que generan resultados reales
            </p>

            {/* Features List */}
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-[#ff6b35]" />
                <span>Consultoría experta</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-[#ff6b35]" />
                <span>Soluciones escalables</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-[#ff6b35]" />
                <span>Soporte 24/7</span>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-400">
              <button 
                onClick={scrollToContact}
                className="group inline-flex items-center gap-3 bg-[#ff6b35] hover:bg-[#f97316] text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-[#ff6b35]/50 hover:scale-105"
              >
                Comenzar ahora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-gray-200 animate-fade-in-up delay-500">
              <div>
                <div className="text-3xl font-bold text-[#ff6b35] mb-1">500+</div>
                <div className="text-sm text-gray-600">Proyectos</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#ff6b35] mb-1">15+</div>
                <div className="text-sm text-gray-600">Años</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#ff6b35] mb-1">98%</div>
                <div className="text-sm text-gray-600">Satisfacción</div>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative lg:h-[600px] animate-fade-in-up delay-300">
            {/* Main card */}
            <div className="relative bg-white border border-gray-200 rounded-2xl p-8 shadow-2xl shadow-orange-500/10">
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-[#ff6b35] to-[#f97316] rounded-xl p-4 shadow-lg shadow-[#ff6b35]/50 animate-float">
                <Sparkles className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <div className="space-y-6">
                {/* Tech Stack */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Stack Tecnológico</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {['React', 'Node.js', 'Python', 'AWS', 'Docker', 'K8s'].map((tech, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center hover:border-[#ff6b35] hover:shadow-sm transition-all">
                        <span className="text-sm text-gray-700 font-medium">{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Services */}
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-gray-900">Nuestros Servicios</h3>
                  {[
                    { name: 'Desarrollo de Software', icon: '💻', progress: 95 },
                    { name: 'Gestión de Nubes', icon: '☁️', progress: 90 },
                    { name: 'Inteligencia Artificial', icon: '🤖', progress: 88 }
                  ].map((service, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span>{service.icon}</span>
                          <span className="text-sm text-gray-700">{service.name}</span>
                        </div>
                        <span className="text-xs text-[#ff6b35] font-semibold">{service.progress}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#ff6b35] to-[#f97316] rounded-full transition-all duration-1000"
                          style={{ width: `${service.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Client badge */}
                <div className="bg-gradient-to-r from-orange-50 to-transparent border border-orange-100 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <span className="text-[#ff6b35] font-bold">+200 empresas</span> confían en nuestras soluciones tecnológicas
                  </p>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -z-10 top-10 right-10 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl"></div>
            <div className="absolute -z-10 bottom-10 left-10 w-72 h-72 bg-orange-300/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#ff6b35] rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[#ff6b35] rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;